import * as React  from "react";
import {XdSentry}  from "@Common/libs/sentry/xd-sentry";
import {Types}     from "@Common/types";

import {cst, cstAssign} from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";

import {Component as FormModal} from "./form-modal";

interface VarStack {
    apis             : Types.Apis.Loaders.XpressoLoadApis,
    formModal        : Types.Xpresso.Account.Office.AddressBookRoute.ComponentNextShippingAddressFormModal,
    handleFormSubmit : (v:Types.Xpresso.Account.Office.AddressBookRoute.ShippingAddress) => Promise<boolean>,
    recordLoad       : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Shipping.GetOfficeShippingAddress.Response>,
}

export const Component = ({
    next
}:Types.Xpresso.Account.Office.AddressBookRoute.ComponentPropsShippingAddressSection) => cst<VarStack>(
    consumeContext("apis"),

    cstAssign("recordLoad", ({apis}, next) => Load2({
        next,
        fetch: async () => {
            const result = await apis.next.shipping.getOfficeShippingAddress();
            return result || null;
        },
    })),

    cstAssign("handleFormSubmit", ({apis, recordLoad}, next) => next(async (updatedValue) => {
        try {
            let result:{error?:string};
            if (recordLoad.value === null)
                result = await apis.next.shipping.addShippingAddress({address: updatedValue});
            else
                result = await apis.next.shipping.updateShippingAddress({id: recordLoad.value.idPrimary, address: updatedValue});

            if (result.error) {
                window.alert(result.error);
                return false;
            }
            else {
                recordLoad.onChange({...recordLoad.value, ...updatedValue});
                return true;
            }
        } catch (error) {
            XdSentry.captureException(error);
            window.alert("An unexpected error occurred.");
            return false;
        }
    })),

    cstAssign("formModal", ({handleFormSubmit, recordLoad}, next) => FormModal({
        next,
        onSubmit : handleFormSubmit,
        value    : recordLoad.value,
    })),

    ({formModal, recordLoad}) => next({
        isLoading : recordLoad.loading,
        render    : () => Content({
            onLinkClick     : formModal.show,
            shippingAddress : recordLoad.value,
        })
    }),
);

interface ContentProps {
    onLinkClick     : (e:React.MouseEvent) => void,
    shippingAddress : Types.Xpresso.Account.Office.AddressBookRoute.ShippingAddress,
}

const Content = ({onLinkClick: handleLinkClick, shippingAddress}:ContentProps) => (
    <div key="shipping" className="section">
        <div className="header">
            <h3 className="title" xd-qa-type="label" xd-qa-name="Office Shipping Address">Office Shipping Address</h3>
            <a onClick={handleLinkClick} xd-qa-type="link" xd-qa-name="Edit Shipping Address" className="edit">{shippingAddress === null ? "Add" : "Edit"}</a>
        </div>
        {shippingAddress !== null && (
            <div className="card">
                <div className="title" xd-qa-type="label" xd-qa-name="Shipping Address Company">
                    {shippingAddress.company}
                </div>
                {shippingAddress.contactName && <div className="subtitle" xd-qa-type="label" xd-qa-name="Shipping Address Contact">
                    {shippingAddress.contactName}
                </div>}
                <div className="vertical-split">
                    <div className="item">
                        <h4 className="label" xd-qa-type="label" xd-qa-name="Address">Address</h4>
                        <span className="value" xd-qa-type="label" xd-qa-name="Address 1">{shippingAddress.address1}</span>
                        {shippingAddress.address2 && <span className="value" xd-qa-type="label" xd-qa-name="Address 2">{shippingAddress.address2}</span>}
                        {(shippingAddress.city || shippingAddress.state || shippingAddress.postalCode) && (
                            <span className="value" xd-qa-type="label" xd-qa-name="City State Zip">
                                {[
                                    shippingAddress.city,
                                    shippingAddress.state,
                                    shippingAddress.postalCode
                                ].filter(Boolean).join(", ")}
                            </span>
                        )}
                    </div>
                    {shippingAddress.contactPhone && (<div className="item">
                        <h5 className="label" xd-qa-type="label" xd-qa-name="Phone">Phone</h5>
                        <span className="value" xd-qa-type="label" xd-qa-name="Contact Phone">{shippingAddress.contactPhone}</span>
                    </div>)}
                </div>
            </div>
        )}
    </div>
);
